import { alpha, createTheme } from '@mui/material';
import {
  borderColor,
  darkBlue,
  error,
  info,
  pink,
  primaryColor,
  secondaryColor,
  success,
  textPlaceholder,
  textPrimaryColor,
  textSecondaryColor,
  warning,
} from '@stack/theme';
import createThemeOverrideButton from '@stack/theme/theme/overrides/button';
import createThemeOverrideChip from '@stack/theme/theme/overrides/chip';
import createThemeOverrideDialogContentText from "@stack/theme/theme/overrides/dialogContentText";
import createThemeOverrideSwitch from "@stack/theme/theme/overrides/switch";
import createThemeOverrideTable from "@stack/theme/theme/overrides/table";
import { ThemeConfig } from '@webbyx/mui';

/**
 * ===========================
 * MAIN
 * ===========================
 */
export const useThemeConfig = (): Partial<ThemeConfig> => {
  return {
    settings: {
      mode: 'light',
      skin: 'bordered',
    },
    overrides: (mode, skin) =>
      createTheme({
        palette: {
          customColors: {
            darkBg: '#282A42',
            lightBg: '#F7F7F9',
            bodyBg: '#F7F7F9',
            trackBg: '#F2F2F4',
            avatarBg: '#F1F1F3',
            avatarText: '#fff',
            tooltipBg: '#262732',
            tableHeaderBg: '#F5F5F7',
          },
          primary: {
            main: primaryColor,
          },
          secondary: {
            main: secondaryColor,
          },
          text: {
            primary: textPrimaryColor,
            secondary: textSecondaryColor,
            disabled: textPlaceholder,
          },
          info: {
            main: info,
          },
          warning: {
            main: warning,
          },
          success: {
            main: success,
          },
          error: {
            main: error,
          },
          pink: {
            main: alpha(pink, 0.1),
            secondary: pink,
            contrastText: pink,
          },
          darkBlue: {
            main: alpha(darkBlue, 0.1),
            secondary: darkBlue,
            contrastText: darkBlue,
          },
          darkError: {
            main: alpha(error, 0.1),
            secondary: error,
            contrastText: error,
          },
          darkInfo: {
            main: alpha(info, 0.1),
            secondary: info,
            contrastText: info,
          },
          darkWarning: {
            main: alpha(warning, 0.1),
            secondary: warning,
            contrastText: warning,
          },
          darkSuccess: {
            main: alpha(success, 0.1),
            secondary: success,
            contrastText: success,
          },
          divider: borderColor,
          grey: {
            100: '#F9FAFB',
            200: '#F3F4F6',
            300: '#E5E7EB',
            400: '#D1D5DB',
            500: '#9CA3AF',
            600: '#6B7280',
            700: '#4B5563',
            800: '#374151',
            900: '#12937',
          },
        },

        breakpoints: {
          values: {
            xs: 0,
            sm: 640,
            md: 768,
            lg: 1024,
            xl: 1480,
          },
        },
        spacing: (factor: number) => `${0.25 * factor}rem`,
        components: {
          MuiLink: {
            styleOverrides: {
              root: {
                color: 'primary',
              },
            },
          },
          MuiDivider: {
            styleOverrides: {
              wrapper: {
                paddingInline: 16,
                color: '#535A68',
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                margin: 0,
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                input: {
                  '&::placeholder': {
                    color: textPlaceholder,
                    fontWeight: 500,
                    opacity: 1,
                  },
                },
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                '.MuiFormLabel-asterisk': { color: '#E63737' },
                color: 'text.secondary',
                fontSize: '0.875rem',
                paddingBottom: 5,
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              root: {
                '.MuiPaper-root': { borderRadius: '10px' },
                '.MuiDialogTitle-root': { lineHeight: 'normal' },
              },
            },
          },
          ...createThemeOverrideTable(mode, skin),
          ...createThemeOverrideButton(mode, skin),
          ...createThemeOverrideChip(mode, skin),
          ...createThemeOverrideDialogContentText(mode, skin),
          ...createThemeOverrideSwitch(mode, skin),
        },
      }),
    enableSubItemIcon: true,
    enableLayoutLoader: true,
    layoutLoaderLasting: 2000,
    excludeComponentStyles: ['chip'],
  };
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export default useThemeConfig;
